import { FunctionComponent, memo, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganisationContext } from '../../../context';
import { NewsFeedInAppMessageData, useInAppMessageReceivedMutation } from '../../../services';
import {
  Button,
  ButtonType,
  parseInAppMessageAction,
  ShareUrlModal,
  useAnalytics,
  useAppDispatch,
} from '../../../shared';
import { inAppMessageRemove } from '../../../slices';

import classes from './InAppMessageFeedCard.module.scss';

interface InAppMessageFeedCardProps {
  message: NewsFeedInAppMessageData;
}

export const InAppMessageFeedCard: FunctionComponent<InAppMessageFeedCardProps> = memo(
  ({ message }) => {
    const { t } = useTranslation();

    const { name: organisationName } = useContext(OrganisationContext).organisation;

    const dispatch = useAppDispatch();

    const { logInAppMessageView } = useAnalytics();

    const [messageReceived] = useInAppMessageReceivedMutation();

    const { id, title, intro, textColor, bgColor, primary, image } = message;

    const {
      text: buttonText,
      textColor: buttonTextColor,
      bgColor: buttonBgColor,
      action,
    } = primary;

    const endMessage = useCallback(async () => {
      await messageReceived({ messageId: id });

      dispatch(inAppMessageRemove(id));
    }, [dispatch, id, messageReceived]);

    const [shareUrl, setShareUrl] = useState<string>('');

    const shareCloseHandler = useCallback(() => {
      setShareUrl('');

      endMessage();
    }, [endMessage]);

    const buttonClickHandler = useCallback(async () => {
      logInAppMessageView({
        iam_id: id,
        iam_action: 'primary',
        time_stamp: new Date().toISOString(),
      });

      const { isShare, isOpenUrl, url } = parseInAppMessageAction(action);

      if (url && isShare) {
        setShareUrl(url);
        return;
      }

      if (url && isOpenUrl) {
        await endMessage();
        window.location.assign(url);
        return;
      }

      if (url) {
        window.open(url, '_blank', 'noreferrer');
      }

      endMessage();
    }, [action, endMessage, id, logInAppMessageView]);

    return (
      <>
        <div
          className={classes['message-card']}
          style={{ backgroundColor: bgColor, color: textColor }}
        >
          <div className={classes['message-card__source']}>
            <span className={classes['message-card__source-label']}>
              <span className={classes['message-card__source-dot']} />
              <span className={classes['message-card__source-name']}>
                {t('inAppMessage.message-from-organisation', { organisationName })}
              </span>
            </span>

            <button
              type={'button'}
              className={classes['message-card__dismiss']}
              aria-label={t('common.close')}
              onClick={endMessage}
            />
          </div>

          {image?.url && (
            <img className={classes['message-card__image']} src={image.url} alt={''} />
          )}

          <div className={classes['message-card__body']}>
            <div className={classes['message-card__title']}>{title}</div>
            <div className={classes['message-card__intro']}>{intro}</div>
            <Button
              type={ButtonType.primary}
              label={buttonText}
              onClick={buttonClickHandler}
              className={classes['message-card__button']}
              style={{
                backgroundColor: buttonBgColor,
                borderColor: buttonBgColor,
                color: buttonTextColor,
              }}
            />
          </div>
        </div>

        <ShareUrlModal isOpen={Boolean(shareUrl)} url={shareUrl} onClose={shareCloseHandler} />
      </>
    );
  }
);
